/**
 * Detail Modal Component
 * Shared modal for all create/edit operations across the application.
 *
 * @typedef {Object} DetailModalConfig
 * @property {string} title - Modal title ("Add Pilot", "Edit Aircraft — ZK-ABC")
 * @property {import('./form-layout.js').FieldDef[]} fields - Form field definitions
 * @property {Object} [values] - Initial values for edit mode (keys match field names)
 * @property {Function} onSave - async (formData) => void — called on save
 * @property {Function} [onDelete] - async () => void — if provided, shows Delete button
 * @property {Function} [onCancel] - () => void — called on cancel/close
 * @property {HTMLElement[]} [customSections] - Additional DOM elements appended after form grid
 */

import { renderFormGrid } from './form-layout.js';

/** @type {HTMLElement|null} Currently open modal backdrop */
let activeBackdrop = null;

/**
 * Close the currently open modal and clean up event listeners.
 */
function closeModal() {
  if (!activeBackdrop) return;
  const backdrop = activeBackdrop;
  activeBackdrop = null;
  backdrop.remove();
  document.removeEventListener('keydown', handleKeyDown);
  document.dispatchEvent(new CustomEvent('detail-modal:closed'));
}

/**
 * Keydown handler for Escape to close and Tab for focus trap.
 * @param {KeyboardEvent} e
 */
function handleKeyDown(e) {
  if (!activeBackdrop) return;

  if (e.key === 'Escape') {
    e.preventDefault();
    closeModal();
    return;
  }

  if (e.key === 'Tab') {
    trapFocus(e);
  }
}

/**
 * Trap focus within the active modal panel.
 * @param {KeyboardEvent} e
 */
function trapFocus(e) {
  if (!activeBackdrop) return;
  const modal = activeBackdrop.querySelector('.detail-modal');
  if (!modal) return;

  const focusable = modal.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.shiftKey) {
    if (document.activeElement === first) {
      e.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }
}

/**
 * Validate required fields and return collected form data.
 * @param {HTMLFormElement} form
 * @param {import('./form-layout.js').FieldDef[]} fields
 * @returns {{ valid: boolean, data: Object, fieldErrors: Object }}
 */
function validateAndCollect(form, fields) {
  const data = {};
  const fieldErrors = {};
  let valid = true;

  for (const field of fields) {
    const el = form.elements[field.name];
    if (!el) continue;

    if (field.type === 'checkbox') {
      data[field.name] = el.checked;
      // Checkboxes are never "required" in the empty-string sense
      continue;
    }

    const value = el.value.trim();
    data[field.name] = value;

    if (field.required && value === '') {
      fieldErrors[field.name] = `${field.label} is required`;
      valid = false;
    }
  }

  return { valid, data, fieldErrors };
}

/**
 * Display field-level error messages in the form.
 * @param {HTMLElement} formContainer
 * @param {Object} fieldErrors - { fieldName: errorMessage }
 */
function showFieldErrors(formContainer, fieldErrors) {
  // Clear all existing errors
  const errorSpans = formContainer.querySelectorAll('.field-error');
  errorSpans.forEach((span) => {
    span.textContent = '';
  });

  // Set new errors
  for (const [fieldName, message] of Object.entries(fieldErrors)) {
    const span = formContainer.querySelector(`.field-error[data-field="${fieldName}"]`);
    if (span) {
      span.textContent = message;
    }
  }
}

/**
 * Populate form fields with initial values.
 * @param {HTMLFormElement} form
 * @param {import('./form-layout.js').FieldDef[]} fields
 * @param {Object} values
 */
function populateValues(form, fields, values) {
  if (!values) return;
  for (const field of fields) {
    const el = form.elements[field.name];
    if (!el || values[field.name] == null) continue;
    if (field.type === 'checkbox') {
      el.checked = Boolean(values[field.name]);
    } else {
      el.value = String(values[field.name]);
    }
  }
}

/**
 * Open a detail modal overlay. Only one modal at a time (single-modal invariant).
 * Closes on Cancel click, backdrop click, or Escape key.
 * On Save: validates fields, calls onSave, closes modal on success,
 * shows field-level errors on validation failure.
 *
 * @param {DetailModalConfig} config
 */
export function openDetailModal(config) {
  const {
    title,
    fields,
    values = null,
    onSave,
    onDelete = null,
    onCancel = null,
    customSections = [],
    readonly = false,
    saveLabel = 'Save',
    extraButtons = [],
  } = config;

  // Enforce single-modal invariant — close any existing modal
  if (activeBackdrop) {
    closeModal();
  }

  // ── Build DOM ──────────────────────────────────────────────────────────

  // Backdrop
  const backdrop = document.createElement('div');
  backdrop.className = 'detail-modal__backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');
  backdrop.setAttribute('aria-label', title);

  // Modal panel
  const modal = document.createElement('div');
  modal.className = 'detail-modal';

  // Header
  const header = document.createElement('div');
  header.className = 'detail-modal__header';

  const titleEl = document.createElement('h2');
  titleEl.className = 'detail-modal__title';
  titleEl.textContent = title;
  header.appendChild(titleEl);

  modal.appendChild(header);

  // Body (scrollable)
  const body = document.createElement('div');
  body.className = 'detail-modal__body';

  // Form
  const form = document.createElement('form');
  form.className = 'detail-modal__form';
  form.noValidate = true;

  // Render form grid from form-layout.js (returns HTML string)
  const formGridHtml = renderFormGrid(fields, values);
  const formGridContainer = document.createElement('div');
  formGridContainer.innerHTML = formGridHtml;
  form.appendChild(formGridContainer);

  // Populate initial values
  populateValues(form, fields, values);

  // Disable all inputs if readonly
  if (readonly) {
    const inputs = form.querySelectorAll('input, select, textarea');
    inputs.forEach((el) => { el.disabled = true; });
  }

  // Append custom sections after form grid
  for (const section of customSections) {
    if (section instanceof HTMLElement) {
      form.appendChild(section);
    }
  }

  body.appendChild(form);
  modal.appendChild(body);

  // Footer with action buttons
  const footer = document.createElement('div');
  footer.className = 'detail-modal__footer';

  // Delete button (optional, left-aligned)
  if (onDelete) {
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'btn btn--danger detail-modal__btn--delete';
    deleteBtn.textContent = 'Delete';
    deleteBtn.addEventListener('click', async () => {
      try {
        await onDelete();
        closeModal();
      } catch (err) {
        // Show error in footer error container
        errorText.textContent = err.message || 'Delete failed';
        footerError.style.display = 'flex';
      }
    });
    footer.appendChild(deleteBtn);
  }

  // Spacer to push Cancel/Save to the right
  const spacer = document.createElement('div');
  spacer.className = 'detail-modal__footer-spacer';
  footer.appendChild(spacer);

  // Footer error container (hidden by default)
  const footerError = document.createElement('div');
  footerError.className = 'detail-modal__footer-error';
  footerError.style.display = 'none';

  // Error icon (circle-exclamation SVG)
  const errorIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  errorIcon.setAttribute('width', '16');
  errorIcon.setAttribute('height', '16');
  errorIcon.setAttribute('viewBox', '0 0 512 512');
  errorIcon.setAttribute('fill', 'currentColor');
  errorIcon.setAttribute('aria-hidden', 'true');
  const iconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  iconPath.setAttribute('d', 'M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1-64 0z');
  errorIcon.appendChild(iconPath);
  footerError.appendChild(errorIcon);

  // Error text span
  const errorText = document.createElement('span');
  errorText.className = 'detail-modal__footer-error-text';
  footerError.appendChild(errorText);

  footer.appendChild(footerError);

  // Cancel / Close button
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn detail-modal__btn--cancel';
  cancelBtn.textContent = readonly ? 'Close' : 'Cancel';
  cancelBtn.addEventListener('click', () => {
    if (onCancel) onCancel();
    closeModal();
  });
  footer.appendChild(cancelBtn);

  // Extra buttons (e.g. Reject)
  for (const btnDef of extraButtons) {
    const extraBtn = document.createElement('button');
    extraBtn.type = 'button';
    extraBtn.className = btnDef.className || 'btn';
    extraBtn.textContent = btnDef.label;
    extraBtn.addEventListener('click', async () => {
      try {
        extraBtn.disabled = true;
        await btnDef.onClick();
        closeModal();
      } catch (err) {
        extraBtn.disabled = false;
        errorText.textContent = err.message || 'Action failed';
        footerError.style.display = 'flex';
      }
    });
    footer.appendChild(extraBtn);
  }

  // Save button (hidden when readonly)
  if (!readonly && onSave) {
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn btn--primary detail-modal__btn--save';
    saveBtn.textContent = saveLabel;
    saveBtn.addEventListener('click', async () => {
      // Clear previous errors
      showFieldErrors(formGridContainer, {});
      footerError.style.display = 'none';
      errorText.textContent = '';

      // Validate
      const { valid, data, fieldErrors } = validateAndCollect(form, fields);
      if (!valid) {
        showFieldErrors(formGridContainer, fieldErrors);
        return;
      }

      // Call onSave
      try {
        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
        await onSave(data);
        // Clear footer error on success before closing
        footerError.style.display = 'none';
        errorText.textContent = '';
        closeModal();
      } catch (err) {
        saveBtn.disabled = false;
        saveBtn.textContent = saveLabel;
        // If the error has field-level info, show it inline; otherwise show in footer
        if (err.fieldErrors) {
          showFieldErrors(formGridContainer, err.fieldErrors);
        } else {
          errorText.textContent = err.message || 'Save failed';
          footerError.style.display = 'flex';
        }
      }
    });
    footer.appendChild(saveBtn);
  }

  modal.appendChild(footer);
  backdrop.appendChild(modal);

  // ── Backdrop click to close ────────────────────────────────────────────
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) {
      if (onCancel) onCancel();
      closeModal();
    }
  });

  // ── Mount and activate ─────────────────────────────────────────────────
  activeBackdrop = backdrop;
  document.body.appendChild(backdrop);
  document.addEventListener('keydown', handleKeyDown);

  // Focus the first focusable element in the modal
  requestAnimationFrame(() => {
    const firstInput = modal.querySelector('input, select, textarea');
    if (firstInput) {
      firstInput.focus();
    } else {
      cancelBtn.focus();
    }
  });
}
